"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { motion } from "framer-motion";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function WeeksSection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  return (
    <section className={cn("relative overflow-clip py-[6rem] lg:py-[16rem]", className)}>
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="right"
      />

      <div className="container flex relative flex-col items-center">
        {/* Centered chip + title */}
        <div className="flex flex-col lg:items-center lg:text-center w-[min(100rem,100%)] lg:sticky lg:top-[8rem]">
          {data?.weeksChip && (
            <motion.div {...fadeInUp()}>
              <Chip
                image={data.weeksChip.icon}
                text={data.weeksChip.text}
              />
            </motion.div>
          )}

          {data?.weeksTitle && (
            <motion.div
              {...fadeInUp(0.1)}
              className="richtext richtext-start lg:richtext-center prose-em:font-ivy prose-em:tracking-[0.3px] prose-em:font-light prose-headings:text-[5rem] prose-headings:font-ivy-headline-regular lg:prose-headings:text-[7rem]">
              <PortableText
                value={data.weeksTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        {/* Items */}
        {data?.weeksItems && data.weeksItems.length > 0 && (
          <div className="w-[min(186rem,100%)] flex flex-col lg:gap-[8rem] lg:mt-[6rem] relative gap-[1.5rem] mt-[4rem]">
            {data.weeksItems.map((item, index) => (
              <div
                key={index}
                className="flex lg:sticky lg:top-[40%]  flex-col !items-center justify-between lg:mt-[8rem] lg:flex-row lg:items-start gap-[4rem] p-[3rem] border-t-[0.4rem] bg-[#112628] lg:h-[61rem]  lg:p-[7rem]"
                style={{ borderColor: item.color || "#D9F9B9" }}>
                <div
                  className={cn(
                    "!items-center !justify-center gap-[1rem] h-[5rem] flex flex-col px-[2rem] prose-p:text-black",
                    "[clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]",
                    "absolute bottom-full left-0 lg:block hidden",
                  )}
                  style={{
                    backgroundColor: `${item?.color}`,
                    left: `${index * 8}rem`,
                  }}>
                  <div className="flex flex-col justify-center items-center h-full">
                    <p className="m-0 leading-none font-geistmono text-[2rem]">0{index + 1}</p>
                  </div>
                </div>

                <div className="w-[min(65.375rem,100%)] flex flex-col">
                  {item?.title && (
                    <div className="richtext richtext-start lg:prose-headings:text-[6rem] prose-headings:font-ivy-headline-regular lg:prose-headings:leading-[100%] prose-headings:text-white prose-headings:leading-[130%]">
                      <PortableText
                        value={item.title}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {item?.text && (
                    <div className="richtext richtext-start mt-[2rem] prose-p:text-white/80">
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>

                {item?.image && (
                  <div className="flex-shrink-0 max-w-[75.25rem]">
                    <Image
                      className="object-cover"
                      src={item.image}
                      alt=""
                      quality={100}
                      width={item.image.width}
                      height={item.image.height}
                      placeholder={item.image.lqip ? "blur" : undefined}
                      blurDataURL={item.image.lqip}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Callout */}
      {data?.weeksCallout && (
        <div className="w-[min(186rem,100%)] mt-[6rem] mx-auto p-[4rem] lg:p-[6rem] items-center flex flex-col">
          {data.weeksCallout.chip && (
            <motion.div {...fadeInUp()}>
              <Chip
                image={data.weeksCallout.chip.icon}
                text={data.weeksCallout.chip.text}
              />
            </motion.div>
          )}
          {data.weeksCallout.title && (
            <motion.div
              {...fadeInUp(0.1)}
              className="richtext richtext-center lg:[&_br]:hidden prose-p:w-[min(69.5rem,100%)] prose-headings:text-black prose-headings:font-ivy-headline-regular lg:prose-headings:text-[7rem] prose-headings:text-[4rem] prose-headings:leading-[100%]">
              <PortableText
                value={data.weeksCallout.title}
                components={richTextComponent}
              />
            </motion.div>
          )}
          {data.weeksCallout.items && data.weeksCallout.items.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1rem] mt-[6rem]">
              {data.weeksCallout.items.map((calloutItem, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp(0.15 + 0.1 * index)}
                  className="flex">
                  <div
                    className={cn(
                      `items-center shrink-0 justify-center gap-[1rem] h-fit pt-[1.5rem] flex lg:w-[6rem] w-[3rem] pb-[3rem] prose-p:text-black`,
                      "[clip-path:polygon(0_0,100%_0,100%_100%,47.45%_100%,0_75.67%)]",
                    )}
                    style={{
                      backgroundColor: `#D9F9B9`,
                    }}>
                    <p className="mb-0 leading-none font-geistmono text-[2rem]">{index + 1}</p>
                  </div>
                  <div className="bg-white border-l-[.25rem] richtext richtext-start p-[1.5rem] pl-[3rem] border-l-[#D9F9B9] prose-p:text-black/80 prose-p:m-0 prose-strong:text-black prose-strong:font-[400]">
                    <PortableText
                      value={calloutItem.text}
                      components={richTextComponent}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
